import { AuthEnums } from "models/enums";
import useAPIClientHelper from "./useAPIClientHelper";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";

function useSettingsService() {
  const { apiClientInstance } = useAPIClientHelper();

  const getCompanySettings = () => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SettingsClient.CompanySettings()
      .then((res) => res.data);
  };

  /**
   * Fetch the current user's profile settings.
   *
   * The standard variant surfaces errors via the global toast. Use the
   * `*Silent` variant for opportunistic/best-effort reads (e.g. background
   * prefill from a widget) where an error toast would be confusing because
   * the user didn't explicitly ask for profile data.
   */
  const getProfileSettings = () => {
    return apiClientInstance(AuthEnums.AuthType.User, {
      showErrors: true,
    })
      .SettingsClient.ProfileSettings()
      .then((res) => res.data);
  };

  const getProfileSettingsSilent = () => {
    return apiClientInstance(AuthEnums.AuthType.User, {
      showErrors: false,
    })
      .SettingsClient.ProfileSettings()
      .then((res) => res.data);
  };

  const updateCompanySettings = (companySettings: WintSuperkollAPIModel.CompanySettingsPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SettingsClient.UpdateCompanySettings(companySettings);
  };

  const getCustomerInvoiceSettings = () => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SettingsClient.InvoiceSettings()
      .then((res) => res.data);
  };

  const updateCustomerInvoiceSettings = (InvoiceSettings: WintSuperkollAPIModel.InvoiceSettingsPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SettingsClient.UpdateInvoiceSettings(InvoiceSettings);
  };

  const getEmployeesSettings = () => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SettingsClient.EmployeeSettings()
      .then((res) => res.data);
  };

  const updateEmployeesSettings = (employeesSettings: WintSuperkollAPIModel.EmployeeSettingsPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SettingsClient.UpdateEmployeeSettings(employeesSettings);
  };

  const getDimensionSettings = () => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SettingsClient.DimensionSettings()
      .then((res) => res.data);
  };

  const updateDimensionSettings = (dimensionSettings: WintSuperkollAPIModel.DimensionSettingPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SettingsClient.UpdateDimensionSettings(dimensionSettings);
  };

  const getIntegrationSettings = () => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SettingsClient.GetApps()
      .then((res) => res.data);
  };

  const createCustomApp = (customApp: WintSuperkollAPIModel.CustomAppPost) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SettingsClient.CreateCustomApp(customApp)
      .then((res) => res.data);
  };

  const updateBillogram = (billogram: WintSuperkollAPIModel.BillogramPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SettingsClient.SaveBillogram(billogram);
  };

  const updateQbis = (qbis: WintSuperkollAPIModel.QbisPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SettingsClient.SaveQbis(qbis);
  };

  const updateJobbile = (jobbile: WintSuperkollAPIModel.JobbilePut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SettingsClient.SaveJobbile(jobbile);
  };

  const updateCustomApp = (id: number, customApp: WintSuperkollAPIModel.CustomAppPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).SettingsClient.UpdateCustomApp(id, customApp);
  };

  const generateCustomAppApiKey = () => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SettingsClient.GenerateCustomAppAPIKey()
      .then((res) => res.data);
  };

  const removeCustomApp = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .SettingsClient.DeleteCustomApp(id)
      .then((res) => res.data);
  };

  return {
    getCompanySettings,
    updateCompanySettings,
    getProfileSettings,
    getProfileSettingsSilent,
    getCustomerInvoiceSettings,
    updateCustomerInvoiceSettings,
    getEmployeesSettings,
    updateEmployeesSettings,
    getDimensionSettings,
    updateDimensionSettings,
    getIntegrationSettings,
    createCustomApp,
    updateBillogram,
    updateQbis,
    updateJobbile,
    updateCustomApp,
    generateCustomAppApiKey,
    removeCustomApp,
  };
}

export default useSettingsService;
